/** Forced at first sign-in and after an administrator reset. */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, inputClass } from '../components/ui';
import { AuthLayout } from './AuthLayout';

export function ChangePasswordPage(): JSX.Element {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      // Every session ended, including this one: sign in again.
      await signOut();
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <Card
        title="Set a new password"
        description={
          user?.mustResetPassword
            ? 'Before you can use the system, choose a password only you know.'
            : 'Choose a new password.'
        }
      >
        <form className="space-y-4" onSubmit={(event) => void onSubmit(event)} noValidate>
          {error && <Alert tone="critical">{error}</Alert>}

          <Field label="Current password" required>
            <input
              className={inputClass}
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>

          <Field label="New password" hint="At least 10 characters." required>
            <input
              className={inputClass}
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>

          <Field label="Repeat new password" required>
            <input
              className={inputClass}
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" busy={busy} className="w-full">
            Change password
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
